"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getStaticUDData = getStaticUDData;
exports.getUserSpecificUDData = getUserSpecificUDData;
const next_config_i18n_1 = require("../../configs/next.config.i18n");
const utils_1 = require("./utils");
/**
 * getStaticUDData()
 *
 * This function is responsible for fetching UD data to render the reactJS application server-side.  Currently,
 * it calls the monolith API fo fetch a block of UD data.
 *
 * The format of the data returned from the server needs to be converted from `snake_case` to `camelCase` before
 * resolving.
 *
 * @param options.locale {string} Locale to send as query param to upstream request
 * @param options.monolithBaseUrl - Base Url of monolith server to request data from
 * @returns
 */
function getStaticUDData(_a) {
    return __awaiter(this, arguments, void 0, function* ({ locale, monolithBaseUrl, }) {
        // Create fetch options
        const fetchOptions = (0, utils_1.defaultStaticOptions)();
        fetchOptions.headers = Object.assign(Object.assign({}, fetchOptions.headers), { [utils_1.REQUESTED_WITH_HEADER_KEY]: utils_1.REQUESTED_WITH_HEADER_VALUE, [utils_1.ACCEPT_LANGUAGE_HEADER_KEY]: locale });
        // Create params
        const params = {
            Config: 'true',
            request: 'true',
            me: 'true',
            visiting: 'true',
            user_specific_tracking: 'true',
            user_agnostic_tracking: 'true',
            experiment: 'true',
            site_stats: 'true',
            // `for_public_caching` is implemented in Django API endpoint to signal fetching of user-specific data
            for_public_caching: 'true',
        };
        // Construct URL
        const url = `${monolithBaseUrl}/api-2.0/contexts/me/?${new URLSearchParams(params)}`;
        // Fetch static (non-user specific) UD data
        const response = yield fetch(url, fetchOptions);
        // Throw an error if there is a problem with the request.  That means we won't be able to render anything anyway and should just 500 overall.
        if (!response.ok) {
            throw new Error(`Response error in getStaticUDData(): ${response.status}`);
        }
        // Convert to JSON.  If an exception occurs, let it take down the request.
        const responseData = yield response.json();
        // Strip marketplace_country and price_country from UDDataConfig so that we're not cacheing geo-specific data
        const agnosticConfig = responseData.Config;
        if (agnosticConfig && agnosticConfig.marketplace_country) {
            agnosticConfig.marketplace_country = undefined;
        }
        if (agnosticConfig && agnosticConfig.price_country) {
            agnosticConfig.price_country = undefined;
        }
        // Map `snake_case` to `camelCase` (with a few legacy exceptions)
        const udData = {
            Config: agnosticConfig,
            experiment: responseData.experiment,
            // This flag is needed for client side ud object refetch with user data
            isGlobalMeContextLoading: true,
            me: responseData.me,
            request: responseData.request,
            site_stats: responseData.site_stats,
            userAgnosticTrackingParams: responseData.user_agnostic_tracking,
            userSpecificTrackingParams: responseData.user_specific_tracking,
            visiting: responseData.visiting,
        };
        // Hack: ensure localization from our application is enforced
        udData.request.locale = locale;
        udData.request.navigation_locale = locale;
        // local development change url.to_root to localhost to use nextjs rewrite proxy
        /* istanbul ignore next */
        if (process.env.DEPLOY_ENV === 'local') {
            udData.Config.url.to_root = `http://localhost:${process.env.PORT || 8080}/`;
        }
        // Hack: force version
        udData.Config.version = `${process.env.VERSION}`;
        // Hack: ensure correct url protocols
        // https://github.com/udemy/website-django/blob/master/udemy/js_global/context_processors.py#L185
        const needRewrite = process.env.DEPLOY_ENV === 'prod';
        if (needRewrite) {
            for (const key in udData.Config.url) {
                if (udData.Config.url.hasOwnProperty(key)) {
                    udData.Config.url[key] = udData.Config.url[key].replace('http://', 'https://');
                }
            }
        }
        // Convert `undefined` -> `null` to avoid serialization errors on `udData`
        return (0, utils_1.convertUndefinedToNull)(udData);
    });
}
/**
 * getUserSpecificUDData()
 *
 * Similar to `getStaticUDData()`, this function is responsible for fetching UD data with user specific
 * information in it.  The data that is requested here likely will vary from what is requested in
 * `getStaticUDData()`.
 *
 * The format of the data returned from the server needs to be converted from `snake_case` to `camelCase` before
 * resolving (same as `getStaticUDData()`)
 *
 * @param options.locale - locale to request
 * @param options.monolithBaseUrl - Base Url of monolith server to request data from
 * @param req - client request
 */
function getUserSpecificUDData(_a, req_1) {
    return __awaiter(this, arguments, void 0, function* ({ locale, monolithBaseUrl }, req) {
        // Create fetch options
        const fetchOptions = (0, utils_1.defaultStaticOptions)();
        // Forward headers
        const forwardedHeaders = (0, utils_1.forwardHeaders)(req);
        fetchOptions.headers = Object.assign(Object.assign({}, fetchOptions.headers), forwardedHeaders);
        // Create params
        const params = {
            visiting: 'true',
            user_specific_tracking: 'true',
            me: 'true',
            request: 'true',
            Config: 'true',
            experiment: 'true',
            browse: 'true',
            // `for_public_caching` is implemented in Django API endpoint to signal fetching of user-specific data
            for_public_caching: 'false',
        };
        // Construct `baseURL`
        const baseUrl = (0, utils_1.getBaseUrl)(req, monolithBaseUrl);
        // Construct url
        const url = `${baseUrl}/api-2.0/contexts/me/?${new URLSearchParams(params)}`;
        // Fetch data
        const response = yield fetch(url, fetchOptions);
        // Throw an error if there is a problem with the request.  That means we won't be able to render anything anyway and should just 500 overall.
        if (!response.ok) {
            throw new Error(`Response error in getUserSpecificUDData(): ${response.status}`);
        }
        // Convert to JSON.  If an exception occurs, let it take down the request.
        const responseData = yield response.json();
        // Hack: force version
        responseData.Config.version = `${process.env.VERSION}`;
        // Hack: force `navigation_locale` to be consistent with set locale
        responseData.request.locale = next_config_i18n_1.i18nPathToBundleMap[locale];
        responseData.request.navigation_locale =
            next_config_i18n_1.i18nPathToBundleMap[locale];
        // Hack: Override `third_party_location` from Monolith API response to ensure path is correctly set
        const thirdPartyLocation = new URL(req.url, monolithBaseUrl);
        // Hack: Remove email query param so PII doesn't get passed to GA
        thirdPartyLocation.searchParams.delete('email');
        responseData.request.third_party_location = thirdPartyLocation.toString();
        // Map `snake_case` to `camelCase` (with a few legacy exceptions)
        const udData = {
            Config: responseData.Config,
            experiment: responseData.experiment,
            me: responseData.me,
            request: responseData.request,
            browse: responseData.browse,
            userSpecificTrackingParams: responseData.user_specific_tracking,
            visiting: responseData.visiting,
        };
        // Ensure loading flags are set to false
        udData.isGlobalMeContextLoading = false;
        udData.me.isLoading = false;
        udData.userSpecificTrackingParams.isLoading = false;
        udData.visiting.isLoading = false;
        // Hack: ensure correct url protocols
        // https://github.com/udemy/website-django/blob/master/udemy/js_global/context_processors.py#L185
        const needRewrite = process.env.DEPLOY_ENV === 'prod';
        if (needRewrite) {
            for (const key in udData.Config.url) {
                if (udData.Config.url.hasOwnProperty(key)) {
                    udData.Config.url[key] = udData.Config.url[key].replace('http://', 'https://');
                }
            }
        }
        return udData;
    });
}
//# sourceMappingURL=ud-data.js.map